import { styled, css } from "@pigment-css/react";
import { clsx } from "clsx";

const ToggleGroupContainer = styled("div")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.managementView.spacing["8"],
  backgroundColor: "transparent",
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: "120px",
  padding: theme.managementView.spacing["4"],
}));

const ToggleGroupButton = styled("button")<{ isActive: boolean }>(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size["14"],
  fontWeight: theme.managementView.font.weight["500"],
  padding: `${theme.managementView.spacing["12"]} ${theme.managementView.spacing["24"]}`,
  border: "none",
  borderRadius: "80px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  whiteSpace: "nowrap",
  backgroundColor: "transparent",
  color: theme.managementView.colors.neutral["56"],
  flex: "1 0 0",

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral["4"],
  },

  "&:focus-visible": {
    outline: `2px solid ${theme.colors.brand[500]}`,
    outlineOffset: "2px",
  },
}));

const activeStyles = css(({ theme }) => ({
  color: theme.managementView.colors.neutral[90],
  fontWeight: theme.managementView.font.weight["500"],
  boxShadow: "none",
  backgroundColor: theme.managementView.colors.neutral["4"],

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral["8"],
  },
}));

export interface ToggleGroupOption {
  value: string;
  label: string;
}

export interface ToggleGroupProps {
  options: ToggleGroupOption[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
}

export const ToggleGroup = ({ options, value, onChange, ariaLabel }: ToggleGroupProps) => {
  return (
    <ToggleGroupContainer role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <ToggleGroupButton
          key={option.value}
          type="button"
          isActive={value === option.value}
          className={clsx(value === option.value && activeStyles)}
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
        >
          {option.label}
        </ToggleGroupButton>
      ))}
    </ToggleGroupContainer>
  );
};
